/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useMemo, useState } from "react";
import { useApp } from "../../../context/AppContext";
import { departmentLabel } from "../../../lib/departmentLabel";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { toast } from "../../ui/sonner";
import { FileText } from "lucide-react";
import { inp, lbl, today, dlBlob, errDetail } from "./assetUtils";

/* 分配使用人 / 办理归还（财务中心） */
export function AssetAssignDialog({ zh, mode, asset, meta, onClose, onSaved }) {
  const { api } = useApp();
  const T = (z, e) => (zh ? z : e);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ employeeId: "", date: today(), note: "", condition: "ok" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const emps = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (meta?.employees || []).filter((e) => !s || [e.name, e.username, e.department, e.employeeId].some((x) => (x || "").toLowerCase().includes(s)));
  }, [meta, q]);
  const isReturn = mode === "return";
  const submit = async () => {
    if (!isReturn && !f.employeeId) return toast.error(T("请选择使用人", "Select an employee"));
    if (isReturn && f.condition !== "ok" && f.note.trim().length < 2) return toast.error(T("损坏 / 丢失请填写说明（维修安排或赔偿处理）", "Describe damage / loss handling"));
    setBusy(true);
    try {
      const { data } = await api.post(`/finance/fixed-assets/${asset.id}/${isReturn ? "return" : "assign"}`, f);
      setDone(data.formId); onSaved();
      toast.success(isReturn ? T("已办理归还", "Returned") : T("已分配使用人", "Assigned"));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const pdf = async () => {
    try { await dlBlob(api, `/finance/fixed-assets/${asset.id}/forms/${done}.pdf`, `${isReturn ? "AssetReturn" : "AssetAssignment"}_${asset.id}.pdf`, true); }
    catch (e) { toast.error(await errDetail(e, T("下载失败", "Download failed"))); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg" data-testid={isReturn ? "asset-return-dialog" : "asset-assign-dialog"}>
      <DialogHeader><DialogTitle>{isReturn ? T("办理归还", "Return Asset") : T("分配使用人", "Assign Asset")}</DialogTitle>
        <DialogDescription><span className="font-semibold text-slate-700">{asset.name}</span> · {asset.id}{asset.serialNo ? ` · SN ${asset.serialNo}` : ""}{isReturn && asset.assignee ? ` · ${T("现使用人", "Assignee")} ${asset.assignee.name}` : ""}</DialogDescription></DialogHeader>
      {done ? (
        <div className="space-y-3" data-testid="asset-form-done">
          <p className="text-sm text-emerald-700 font-semibold">{isReturn ? T("归还已登记。", "Return recorded.") : T("分配已登记。", "Assignment recorded.")}</p>
          <Button variant="outline" data-testid="asset-form-pdf-btn" onClick={pdf} className="gap-1.5"><FileText className="h-4 w-4" />{isReturn ? T("打开《资产归还单》PDF", "Open Return Form PDF") : T("打开《资产领用单》PDF", "Open Assignment Form PDF")}</Button>
          <div className="flex justify-end"><Button onClick={onClose} data-testid="asset-form-close-btn">{T("关闭", "Close")}</Button></div>
        </div>
      ) : (
        <div className="space-y-3">
          {!isReturn && (
            <div>
              <input className={inp} data-testid="asset-emp-search" placeholder={T("搜索姓名 / 账号 / 部门…", "Search name / account / dept…")} value={q} onChange={(e) => setQ(e.target.value)} />
              <select className={`${inp} h-40`} size={6} data-testid="asset-emp-select" value={f.employeeId} onChange={(e) => set("employeeId", e.target.value)}>
                {emps.map((e) => <option key={e.employeeId} value={e.employeeId}>{e.name}（{departmentLabel(e.department, { id: e.department }) || "—"} · {e.branch}{e.username ? ` · ${e.username}` : ""}）</option>)}
              </select>
              {emps.length === 0 && <p className="text-xs text-slate-400 mt-1">{T("无匹配员工（仅显示在职员工）", "No matching active employees")}</p>}
            </div>
          )}
          {isReturn && (
            <div className="flex gap-2" data-testid="asset-return-conditions">
              {Object.keys(meta?.conditions || {}).map((k) => (
                <button key={k} type="button" data-testid={`asset-cond-${k}`} aria-pressed={f.condition === k} onClick={() => set("condition", k)}
                  className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex-1 text-sm rounded-lg border px-3 py-2 font-semibold ${f.condition === k ? (k === "ok" ? "bg-emerald-600 text-white border-emerald-600" : k === "damaged" ? "bg-amber-500 text-white border-amber-500" : "bg-red-600 text-white border-red-600") : "bg-white border-slate-200 text-slate-600"}`}>{lbl(meta.conditions, k, zh)}</button>
              ))}
            </div>
          )}
          <label className="text-sm text-slate-600 block">{isReturn ? T("归还日期", "Return date") : T("分配日期", "Assign date")}<input type="date" className={inp} data-testid="asset-form-date" value={f.date} onChange={(e) => set("date", e.target.value)} /></label>
          <label className="text-sm text-slate-600 block">{T("说明", "Note")}{isReturn && f.condition !== "ok" ? " *" : ""}<input className={inp} data-testid="asset-form-note" value={f.note} onChange={(e) => set("note", e.target.value)} placeholder={isReturn ? (f.condition === "lost" ? T("如：按残值赔偿 $300，已从 Final Pay 扣除", "e.g. compensation $300") : f.condition === "damaged" ? T("如：屏幕碎裂，送修", "e.g. screen cracked, sent for repair") : T("可选", "Optional")) : T("如：入职配发", "e.g. onboarding issue")} /></label>
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2"><Button variant="outline" onClick={onClose}>{T("取消", "Cancel")}</Button><Button data-testid="asset-form-submit" disabled={busy} className={isReturn ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"} onClick={submit}>{isReturn ? T("确认归还", "Confirm Return") : T("确认分配", "Confirm Assign")}</Button></div>
        </div>
      )}
    </DialogContent></Dialog>
  );
}
